import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useRunPage, useRunPicker, useDebounced } from "./useRunData";
import { GhostCards } from "./Pending";
import ResponseMarkdown, { SourcesList } from "../../components/ResponseMarkdown";
import "./TabResponses.css";

const PAGE_SIZE = 50;
const EMPTY_FILTERS = { model: "", mentioned: "all", prompt: "", competitor: "", q: "" };

export default function TabResponses() {
  const { report, reportId } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [runNumber, setRunNumber, runs] = useRunPicker(report);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const q = useDebounced(filters.q, 300);

  // Deep link: ?run=N
  const deepLinkRun = searchParams.get("run");
  useEffect(() => {
    if (deepLinkRun && Number(deepLinkRun) !== runNumber) setRunNumber(Number(deepLinkRun));
  }, [deepLinkRun, runNumber, setRunNumber]);

  // Filtering and paging happen on the server; the browser never downloads the full run.
  const { results, total, facets, pending, loading, error } = useRunPage(
    reportId, runNumber, { ...filters, q }, page, PAGE_SIZE,
  );
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const set = (k) => (e) => { setFilters((f) => ({ ...f, [k]: e.target.value })); setPage(1); };

  // Menus come from the report doc until the run's own facets arrive.
  const modelOptions = facets?.models?.length ? facets.models : (report.models || []);
  const competitorOptions = facets?.competitors?.length ? facets.competitors : (report.competitors || []);

  return (
    <div className="tab-responses">
      <div className="run-picker">
        <label>Run:
          <select value={runNumber || ""} onChange={(e) => { setRunNumber(Number(e.target.value)); setPage(1); }}>
            {runs.slice().reverse().map((r) => (
              <option key={r.runNumber} value={r.runNumber}>
                #{r.runNumber} — {r.completedAt ? new Date(r.completedAt).toLocaleString() : r.status}
              </option>
            ))}
          </select>
        </label>
        <label>Model:
          <select value={filters.model} onChange={set("model")}>
            <option value="">All</option>
            {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>Show:
          <select value={filters.mentioned} onChange={set("mentioned")}>
            <option value="all">All</option>
            <option value="mentioned">Mentioned</option>
            <option value="not">Not mentioned</option>
          </select>
        </label>
        <label>Prompt:
          <select value={filters.prompt} onChange={set("prompt")}>
            <option value="">All</option>
            {(report.prompts || []).map((p) => (
              <option key={p.number} value={p.number}>#{p.number} {p.text.slice(0, 40)}{p.text.length > 40 ? "…" : ""}</option>
            ))}
          </select>
        </label>
        <label>Competitor:
          <select value={filters.competitor} onChange={set("competitor")}>
            <option value="">Any</option>
            {competitorOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <input
          type="search"
          className="response-search"
          placeholder="Search responses…"
          aria-label="Search responses"
          value={filters.q}
          onChange={set("q")}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className={`response-list ${loading && !pending ? "is-refreshing" : ""}`}>
        {pending && <GhostCards count={3} />}
        {!pending && (results || []).map((r, i) => (
          <article key={`${r.model}-${r.prompt_number}-${i}`} className={`response-card ${r.client_mentioned ? "mentioned" : ""}`}>
            <header>
              <strong>{r.model}</strong>
              <span className="muted"> · prompt #{r.prompt_number}</span>
              {r.city && <span className="muted"> · {r.city}, {r.cityStateAbbr}</span>}
              <span className={`tag ${r.client_mentioned ? "tag-hit" : ""}`}>
                {r.client_mentioned ? "mentioned" : "no mention"}
              </span>
            </header>
            <ResponseMarkdown text={r.response} className="response-text" citations={r.citations} />
            <SourcesList citations={r.citations} urls={r.urls_referenced} text={r.response} />
            {r.competitors_mentioned?.length > 0 && (
              <div className="competitors">Competitors: {r.competitors_mentioned.join(", ")}</div>
            )}
          </article>
        ))}
        {!pending && results && results.length === 0 && <div>No responses match the current filters.</div>}
      </div>

      {!pending && total > 0 && (
        <div className="pager">
          <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {pageCount} · {total.toLocaleString()} responses</span>
          <button disabled={page >= pageCount || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
